/* eslint-disable react-hooks/exhaustive-deps, react/forbid-component-props, @next/next/no-img-element, sonarjs/cognitive-complexity */
/* global ApplePaySession */
import applePay from 'braintree-web/apple-pay';
import dataCollector from 'braintree-web/data-collector';
import classNames from 'classnames';
import { Field, Formik } from 'formik';
import upperCase from 'lodash/upperCase';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import {
  PAYMENT_DOD_TRACKING_GATEWAY,
  PAYMENT_METHODS,
  PAYMENT_TOP_CTA_ID,
  PAYMENT_TYPES,
} from '../../../brands/default/constants';
import { CHECKOUT_PAYMENT_COMPONENT } from '../../../brands/default/content/components/checkout';
import { BUTTON_IMG_APPLE_PAY } from '../../../config/constants/images';
import { trackEvent, updateDod } from '../../../helpers/analytics';
import BraintreeService from '../../../helpers/braintreeService';
import CustomErrorsService from '../../../helpers/customErrorsService';
import ErrorTrackingService from '../../../helpers/errorTrackingService';
import Message, { MESSAGE_TYPES } from '../../_generic/Message';
import CTAButton from '../../_generic/button/CTAButton';
import ClientOnlyPortal from '../../_generic/portal/ClientOnlyPortal';
import Spinner from '../../_generic/spinner/Spinner';
import useToast from '../../_generic/toast/UseToast';
import BasketSocialCues from '../BasketSocialCues';
import ButtonGoToPayment from './ButtonGoToPayment';
import PaymentAgreeTerms from './PaymentAgreeTerms';
import PaymentSummary from './PaymentSummary';
import VipMessageWrapper from './VipMessageWrapper';
import VipToggleButtons from './VipToggleButtons';
import VoucherContractDisclamer from './VoucherContractDisclamer';

const PaymentApple = ({
  afterError,
  atolProtected,
  basketProducts,
  currencySymbol,
  customError,
  finalOrderAmount,
  isExclusiveProductInBasket,
  isVipUser,
  isWalletApplied,
  orderComplete,
  orderSummary,
  payFinalOrderAmount,
  providerError,
  setVipOptIn,
  totalQuantity,
  travelDisableButton,
  vipOptIn = null,
  vipOptInFinalOrderAmount,
  vipPreButtonMessage,
  welcomeVipEnabled,
  vipPaywall,
  vipSubscriptionMessage,
  isSecondCheckout,
}) => {
  const formikRef = React.useRef(null);
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [paymentInProcess, setPaymentInProcess] = useState(false);
  const [instance, setInstance] = useState(null);
  const [dataCollectorInstance, setDataCollectorInstance] = useState(null);

  const handleVipToggleClick = (vipOptInValue) => {
    setVipOptIn(vipOptInValue);
  };

  const toggleLoaderOn = () => {
    setIsLoading(true);
  };

  const toggleLoaderOff = () => {
    setIsLoading(false);
  };

  const notifyError = (message) => {
    toast.addToast(message, 'toast-error', 'top-right');
  };

  const handleOrderComplete = (order) => {
    if (dataCollectorInstance && dataCollectorInstance.teardown) {
      dataCollectorInstance.teardown();
    }
    toggleLoaderOff();
    // call order complete in payment component
    orderComplete(order);
  };

  const handleOrderError = (error) => {
    if (dataCollectorInstance && dataCollectorInstance.teardown) {
      dataCollectorInstance.teardown();
    }

    if (
      error.data &&
      error.data.errors &&
      Object.keys(error.data.errors).length > 0
    ) {
      // custom errors
      const customErrors = CustomErrorsService.parseErrors(
        error.data.errors,
        error.config.data || {},
      );
      customError({ errors: customErrors });
    } else {
      // default error message
      notifyError(CHECKOUT_PAYMENT_COMPONENT.paymentApple.errorDefaultPayment);
    }

    toggleLoaderOff();
    setPaymentInProcess(false);

    // will reload the checkout
    afterError();
  };

  const submitPayment = (credit) => {
    BraintreeService.totalAmountToPay = payFinalOrderAmount;

    setPaymentInProcess(true);
    providerError({});
    toggleLoaderOn();
    trackEvent('applepay_click');
    if (welcomeVipEnabled && vipOptIn) trackEvent('vip_applepaybw_click');
    if (welcomeVipEnabled && !vipOptIn) trackEvent('vip_applepaybwo_click');
    updateDod({ gateway: PAYMENT_DOD_TRACKING_GATEWAY.braintree });
    if (payFinalOrderAmount > 0) {
      const paymentRequest = instance.createPaymentRequest({
        countryCode: upperCase(process.env.NEXT_PUBLIC_COUNTRY_CODE),
        currencyCode: upperCase(orderSummary?.currency),
        total: {
          amount: payFinalOrderAmount.toFixed(2).toString(),
          label: process.env.NEXT_PUBLIC_BRAND_FRONTEND,
        },
      });

      const session = new ApplePaySession(3, paymentRequest);

      session.onvalidatemerchant = async (event) => {
        try {
          const merchantSession = await instance.performValidation({
            displayName: process.env.NEXT_PUBLIC_BRAND_FRONTEND,
            validationURL: event.validationURL,
          });
          if (merchantSession) {
            session.completeMerchantValidation(merchantSession);
          }
        } catch (error) {
          // You should show an error to the user, e.g. 'Apple Pay failed to load.'
          console.error('Error validating merchant:', error);
          notifyError(
            CHECKOUT_PAYMENT_COMPONENT.paymentApple.errorDefaultPayment,
          );
          session.abort();
          toggleLoaderOff();
        }
      };

      session.onpaymentauthorized = async (event) => {
        try {
          const tokenizationPayload = await instance.tokenize({
            token: event.payment.token,
          });
          if (tokenizationPayload) {
            session.completePayment(ApplePaySession.STATUS_SUCCESS);

            BraintreeService.preparePayment({
              basketProducts,
              credit,
              dataCollectorInstance,
              isWalletApplied,
              orderSummary,
              paymentMethod: PAYMENT_METHODS.applePay,
              tokenizationPayload,
            });
          }
        } catch (error) {
          setPaymentInProcess(false);
          console.error('Error tokenizing Apple Pay:', error);
          notifyError(
            CHECKOUT_PAYMENT_COMPONENT.paymentApple.errorDefaultPayment,
          );
          session.completePayment(ApplePaySession.STATUS_FAILURE);
          toggleLoaderOff();
        }
      };

      session.oncancel = () => {
        console.error('Payment cancelled');
        notifyError(`Payment aborted`);
        toggleLoaderOff();
        session.abort();
      };
      session.begin();
    } else {
      const tokenizationPayload = {
        nonce: null,
      };
      BraintreeService.preparePayment({
        basketProducts,
        credit,
        dataCollectorInstance,
        isWalletApplied,
        orderSummary,
        paymentMethod: PAYMENT_METHODS.applePay,
        tokenizationPayload,
      });
    }
  };

  const createApplePayInstance = async (clientInstance) => {
    if (!clientInstance) return;
    try {
      const applePayInstance = await applePay.create({
        client: clientInstance,
      });
      setInstance(applePayInstance);
      toggleLoaderOff();
    } catch (error) {
      console.error(`Error creating Apple Pay Instance: ${error}`);
      notifyError(CHECKOUT_PAYMENT_COMPONENT.paymentApple.errorBraintree);
      trackEvent('applepay_aborted');
      toggleLoaderOff();
    }
  };

  const setupMethods = async (clientInstance) => {
    if (!clientInstance) return;
    try {
      const response = await dataCollector.create({
        client: clientInstance,
        kount: true,
        paypal: true,
      });

      if (response) {
        // At this point, you should access the dataCollectorInstance.deviceData value and provide it
        // to your server, e.g. by injecting it into your form as a hidden input.
        setDataCollectorInstance(response);
        BraintreeService.deviceData = response.deviceData;
      }

      createApplePayInstance(clientInstance);
    } catch (error) {
      ErrorTrackingService.logError(error);
      toggleLoaderOff();
    }
  };

  const setupBraintree = async () => {
    // payment component will be disabled if user is not logged in
    BraintreeService.paymentComplete = handleOrderComplete;
    BraintreeService.paymentError = handleOrderError;
    BraintreeService.paymentType = PAYMENT_TYPES.apple;
    BraintreeService.totalQuantity = totalQuantity;
    BraintreeService.providerError = providerError;
    const clientInstance = await BraintreeService.getClientInstance();
    if (clientInstance) {
      setupMethods(clientInstance);
    }
  };

  useEffect(() => {
    toggleLoaderOn();
    setupBraintree();
    formikRef.current.validateForm();
  }, [payFinalOrderAmount, vipOptIn]);

  return (
    <>
      {isLoading && <Spinner isFullScreen />}
      <Formik
        enableReinitialize
        initialValues={{
          cardCvv: '',
          confirmPayment: true,
          isVipOptedIn: vipOptIn,
        }}
        innerRef={formikRef}
        onSubmit={(values, { setSubmitting }) => {
          setSubmitting(false);
        }}
        validate={(values) => {
          const errors = {};
          if (!values.confirmPayment) {
            errors.confirmPayment = true;
          }
          if (!isVipUser && welcomeVipEnabled && vipOptIn === null) {
            errors.isVipOptedIn = true;
          }

          return errors;
        }}
      >
        {({ handleSubmit, isValid, isSubmitting }) => (
          <form className="" name="payment" onSubmit={handleSubmit}>
            <div className="row payment-body">
              <div className="col-12">
                {CHECKOUT_PAYMENT_COMPONENT.paymentApple.formMessage}
                <PaymentAgreeTerms disabled={false} />
                {!vipPaywall && (
                  <BasketSocialCues
                    atolProtected={atolProtected}
                    className="with-margin"
                    isLighter
                    isPostCheckout={BraintreeService.isPostCheckout}
                  />
                )}
                <VoucherContractDisclamer />
                <PaymentSummary
                  {...orderSummary}
                  isPostCheckout={BraintreeService.isPostCheckout}
                  totalQuantity={totalQuantity}
                />
              </div>
              {!isVipUser && welcomeVipEnabled && vipPreButtonMessage && (
                <VipMessageWrapper>{vipPreButtonMessage}</VipMessageWrapper>
              )}
              {!isVipUser && welcomeVipEnabled && (
                <>
                  <VipToggleButtons
                    currencySymbol={currencySymbol}
                    finalOrderAmount={finalOrderAmount}
                    isExclusiveProductInBasket={isExclusiveProductInBasket}
                    setPayButton={handleVipToggleClick}
                    vipOptInFinalOrderAmount={vipOptInFinalOrderAmount}
                    vipSubscriptionMessage={vipSubscriptionMessage}
                  />
                  <Field
                    id="isVipOptedIn"
                    name="isVipOptedIn"
                    type="hidden"
                    value={vipOptIn || ''}
                  />
                  <div className="vip-messages">
                    {isExclusiveProductInBasket && (
                      <div className="vip-pay-button-message">
                        <Message
                          message={
                            CHECKOUT_PAYMENT_COMPONENT.paymentApple
                              .vipPayExclusiveDeal
                          }
                        />
                      </div>
                    )}
                    {isExclusiveProductInBasket &&
                      vipOptIn === null &&
                      !isValid && (
                        <div className="vip-pay-button-message">
                          <Message
                            message={
                              CHECKOUT_PAYMENT_COMPONENT.paymentApple
                                .vipPayExclusiveButton
                            }
                            showIcon={false}
                            type={MESSAGE_TYPES.error}
                          />
                        </div>
                      )}
                    {!isExclusiveProductInBasket &&
                      vipOptIn === null &&
                      !isValid && (
                        <div className="vip-pay-button-message">
                          <Message
                            message={
                              CHECKOUT_PAYMENT_COMPONENT.paymentApple
                                .vipPayButtonMessage
                            }
                            showIcon={false}
                            type={MESSAGE_TYPES.error}
                          />
                        </div>
                      )}
                    {vipOptIn !== null && (
                      <div className="vip-pay-button-message">
                        {
                          CHECKOUT_PAYMENT_COMPONENT.paymentApple
                            .vipPayButtonMessageSelected
                        }
                      </div>
                    )}
                  </div>
                </>
              )}
              <div className="col-12">
                <div
                  className={classNames({
                    'vip-pay-button-container': !isVipUser && welcomeVipEnabled,
                  })}
                >
                  <CTAButton
                    className="full-width black-button"
                    data-qa="payWithApplePay"
                    disabled={
                      travelDisableButton ||
                      paymentInProcess ||
                      isSubmitting ||
                      !isValid
                    }
                    onClick={() => submitPayment(false)}
                  >
                    {CHECKOUT_PAYMENT_COMPONENT.paymentApple.buttonText}&nbsp;
                    <img
                      alt={CHECKOUT_PAYMENT_COMPONENT.paymentApple.imageText}
                      src={BUTTON_IMG_APPLE_PAY}
                      style={{ width: '55px' }}
                    />
                  </CTAButton>
                </div>
              </div>
              {isSecondCheckout && vipSubscriptionMessage && (
                <div>{vipSubscriptionMessage}</div>
              )}
            </div>
          </form>
        )}
      </Formik>
      <ClientOnlyPortal targetElementId={PAYMENT_TOP_CTA_ID}>
        <ButtonGoToPayment />
      </ClientOnlyPortal>
      <style jsx>{`
        .vip-pay-button-container {
          margin: 0 auto 10px auto;
          width: 75%;
        }
        .vip-messages {
          margin: 0 auto;
          padding: 0 10px;
        }
        .vip-pay-button-message {
          margin: 0 auto 10px auto;
          text-align: center;
        }
      `}</style>
    </>
  );
};

PaymentApple.propTypes = {
  afterError: PropTypes.func.isRequired,
  atolProtected: PropTypes.bool,
  basketProducts: PropTypes.array.isRequired,
  currencySymbol: PropTypes.string,
  customError: PropTypes.func.isRequired,
  finalOrderAmount: PropTypes.number,
  isExclusiveProductInBasket: PropTypes.bool,
  isSecondCheckout: PropTypes.bool,
  isVipUser: PropTypes.bool,
  isWalletApplied: PropTypes.bool,
  orderComplete: PropTypes.func.isRequired,
  orderSummary: PropTypes.object.isRequired,
  payFinalOrderAmount: PropTypes.number,
  providerError: PropTypes.func.isRequired,
  setVipOptIn: PropTypes.func,
  totalQuantity: PropTypes.number,
  travelDisableButton: PropTypes.bool,
  vipOptIn: PropTypes.bool,
  vipOptInFinalOrderAmount: PropTypes.number,
  vipPaywall: PropTypes.bool,
  vipPreButtonMessage: PropTypes.element,
  vipSubscriptionMessage: PropTypes.element,
  welcomeVipEnabled: PropTypes.bool,
};

export default PaymentApple;
